import React, { useCallback, useState } from 'react';
import {
  Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { PALETTE, axisProps, fmtMoney, fmtMoneyShort, fmtNum, tooltipStyle } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Billed revenue over time, split into its two streams. This is the accrual view (what was
// billed, by when it was billed); Daily Collection is the cash view (what was received).

const COLUMNS = [
  { key: 'bucket', label: 'Period' },
  { key: 'opdNet', label: 'OPD Fees (net)', align: 'right', render: (r) => fmtMoney(r.opdNet) },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'invoiced', label: 'Invoiced', align: 'right', render: (r) => fmtMoney(r.invoiced) },
  { key: 'invoices', label: 'Invoices', align: 'right' },
  { key: 'total', label: 'Total', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.total)}</span>, csv: (r) => r.total },
];

const RevenueTrendReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [granularity, setGranularity] = useState('daily');

  const fetcher = useCallback(
    () => reportsAPI.getRevenueTrend({ from: range.from, to: range.to, granularity }),
    [range, granularity]
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Total Revenue', value: fmtMoney(k.total ?? 0), sub: 'billed in period' },
    { label: 'OPD Fees (net)', value: fmtMoney(k.opdNet ?? 0) },
    { label: 'Invoiced', value: fmtMoney(k.invoiced ?? 0), sub: 'pharmacy + services' },
    { label: 'Visits', value: fmtNum(k.visits ?? 0) },
    { label: granularity === 'monthly' ? 'Months' : 'Days with Revenue', value: fmtNum(k.buckets ?? 0) },
  ];

  const series = (data?.series || []).map((b) => ({
    ...b, opdNet: Number(b.opdNet), invoiced: Number(b.invoiced), total: Number(b.total),
  }));

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {['daily', 'monthly'].map((g) => (
            <button
              key={g}
              onClick={() => setGranularity(g)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                granularity === g ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">
          This is what was billed. For cash actually received on a given day, use Daily Collection.
        </p>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="Revenue over time - OPD vs invoiced">
        {series.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">No revenue in the selected range</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={series} margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
              <XAxis dataKey="bucket" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={fmtMoneyShort} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Area type="monotone" dataKey="opdNet" name="OPD fees" stackId="1" stroke={PALETTE[0]} fill={PALETTE[0]} fillOpacity={0.65} />
              <Area type="monotone" dataKey="invoiced" name="Invoiced" stackId="1" stroke={PALETTE[1]} fill={PALETTE[1]} fillOpacity={0.55} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'period', value: r.bucket, granularity, from: range.from, to: range.to })}
        title="Revenue by period"
        columns={COLUMNS}
        rows={data?.series || []}
        csvName={`revenue-trend-${range.from}-to-${range.to}`}
      />

      <ReportPrintLayout
        title="Revenue Trend"
        subtitle={`Period: ${range.from} to ${range.to} (${granularity})`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[{ title: 'Revenue by period', columns: COLUMNS, rows: data?.series || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default RevenueTrendReport;
